import { createFileRoute } from '@tanstack/react-router';
import { lazy, Suspense } from 'react';
import { z } from 'zod';

import { Screens } from '~/constants/config/permissions';
import { CatalogPageSkeleton } from '~/features/content/components/catalog/catalog-page-skeleton';
import { QcFullPage } from '~/features/content/components/qc/qc-full-page';
import {
  CATALOG_PRODUCT_LAYOUTS,
  CATALOG_RESULTS_VIEWS,
  CATALOG_SAMPLE_VIEWS,
  CATALOG_STEPS,
} from '~/features/content/utils/catalog-steps';
import { ISSUE_SOURCES } from '~/features/content/utils/issue-source';
import { QC_PRODUCT_SORTS, QC_SAVED_VIEWS } from '~/features/content/utils/qc-products';
import { QUALITY_VIEWS } from '~/features/content/utils/quality-labels';
import { requirePermission } from '~/utils/guards';

const CatalogJobPage = lazy(() => import('~/features/content/pages/catalog-job-page'));

// The step, the Results sub-view and each view's filters live in the URL, so a reload, a shared
// link or the back button lands on the same view. Product / worksheet / quality keys match QC's.
const searchSchema = z.object({
  step: z.enum(CATALOG_STEPS).optional().catch(undefined),
  rview: z.enum(CATALOG_RESULTS_VIEWS).optional().catch(undefined),
  /** Finished sample: Overview · Products · Problems · Gap check. */
  sview: z.enum(CATALOG_SAMPLE_VIEWS).optional().catch(undefined),
  /** Products layout (Sample and Results): table (default) or grid. */
  playout: z.enum(CATALOG_PRODUCT_LAYOUTS).optional().catch(undefined),
  // Products table — saved view, sort and filters (the QC products table's keys).
  psv: z.enum(QC_SAVED_VIEWS).optional().catch(undefined),
  psort: z.enum(QC_PRODUCT_SORTS).optional().catch(undefined),
  ptype: z.string().max(100).optional().catch(undefined),
  pins: z.string().max(200).optional().catch(undefined),
  pcheck: z.string().max(200).optional().catch(undefined),
  pfield: z.string().max(100).optional().catch(undefined),
  psource: z.enum(ISSUE_SOURCES).optional().catch(undefined),
  pq: z.string().max(100).optional().catch(undefined),
  /** Products: one cell of the status × type grid (run-product status and vertical). */
  pstatus: z.string().max(40).optional().catch(undefined),
  // Attribute worksheet — the picked product type and the attribute open in its drawer.
  wtype: z.string().max(100).optional().catch(undefined),
  wattr: z.string().max(200).optional().catch(undefined),
  // Quality — channel, "View as" and batch, as on the job's Quality tab.
  qplatform: z.string().optional().catch(undefined),
  qview: z.enum(QUALITY_VIEWS).optional().catch(undefined),
  qbatch: z.coerce.number().int().min(0).optional().catch(undefined),
});

/** One catalog job at `/content/catalog/$jobId` (a full page outside the Content tabs) — Set up · Sample · Full run · Results. */
export const Route = createFileRoute('/_authenticated/_tenant/content_/catalog/$jobId')({
  // Outside the Content layout (its own full page), so it repeats the section's screen guard.
  beforeLoad: requirePermission(Screens.CONTENT_GENERATION),
  validateSearch: searchSchema,
  component: CatalogJobRoute,
});

function CatalogJobRoute() {
  const { jobId } = Route.useParams();
  const { step, ...rest } = Route.useSearch();
  return (
    <QcFullPage>
      <Suspense fallback={<CatalogPageSkeleton />}>
        <CatalogJobPage jobId={jobId} step={step} search={rest} />
      </Suspense>
    </QcFullPage>
  );
}
